import React, { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  ARCH_EDGES,
  ARCH_LAYERS,
  ARCH_NODES,
  type EdgeKind,
  edgePath,
  edgesFor,
  getNode,
  MAP_NODE_HEIGHT,
  MAP_NODE_WIDTH,
  MAP_VIEWBOX,
  MAP_LAYER_LABEL_WIDTH,
  nodeCentre,
  nodesByLayer,
  relatedNodeIds,
} from "@/data/architecture";
import { ProtocolMapNode } from "./ProtocolMapNode";
import { ProtocolMapPanel } from "./ProtocolMapPanel";
import { ProtocolMapLegend } from "./ProtocolMapLegend";

interface ProtocolMapProps {
  /**
   * "full" — the interactive component (default, /protocol).
   * "preview" — non-interactive, reduced height, gradient fade. Used on Home.
   */
  variant?: "full" | "preview";
  /**
   * In preview mode, where the "explore" CTA points. Defaults to
   * `/protocol#system-map`.
   */
  exploreHref?: string;
}

/** Stroke styling — kept in sync with ProtocolMapLegend. */
function strokeProps(kind: EdgeKind): {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
} {
  switch (kind) {
    case "governs":
      return { stroke: "var(--color-ember)", strokeWidth: 1.2 };
    case "enforces":
      return { stroke: "var(--color-claude)", strokeWidth: 1.4 };
    case "anchors-to":
      return { stroke: "var(--color-ink)", strokeWidth: 1.2 };
    case "stored-in":
      return {
        stroke: "var(--color-ink-3)",
        strokeWidth: 1,
        strokeDasharray: "5 4",
      };
  }
}

const ELEVATOR_PITCH =
  "A .person is a signed file describing a self — held under cryptographic keys the person owns, anchored to a permissionless chain, stored at addresses the person controls, and surfaced wherever they choose to appear. The Foundation guarantees, in perpetuity, that no one — including the protocol's authors — can override a person's twelve irrevocable rights.";

export function ProtocolMap({
  variant = "full",
  exploreHref = "/protocol#system-map",
}: ProtocolMapProps) {
  const interactive = variant === "full";
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);

  const activeId = focusId ?? hoverId;
  const focusedNode = focusId ? getNode(focusId) : null;

  const related = useMemo<Set<string> | null>(
    () => (activeId ? relatedNodeIds(activeId) : null),
    [activeId],
  );

  const handleNavigateTo = useCallback((id: string) => {
    setFocusId(id);
    setHoverId(null);
  }, []);

  const handleSchemaDeepLink = useCallback((schemaDomainId: string) => {
    if (typeof window === "undefined") return;
    window.history.pushState(null, "", `#${schemaDomainId}`);
    // Notify SchemaExplorer (which listens for hashchange):
    window.dispatchEvent(new HashChangeEvent("hashchange"));
    // Scroll the schema explorer into view:
    const el = document.querySelector(`[data-schema-explorer]`);
    if (el && el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Keyboard: Esc clears focus.
  useEffect(() => {
    if (!interactive) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setFocusId(null);
        setHoverId(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [interactive]);

  return (
    <div
      className={cn(
        "border border-hairline bg-paper-2/30",
        variant === "full" ? "p-4 md:p-6" : "p-3 md:p-4",
      )}
    >
      {variant === "full" && (
        <div className="mb-5 pb-4 border-b border-hairline flex flex-wrap items-baseline gap-3 justify-between">
          <div className="flex items-baseline gap-3 font-mono text-[10px] uppercase tracking-widest text-ink-3">
            <span className="text-ink font-semibold">SYSTEM MAP</span>
            <span className="border-l border-hairline pl-3">interactive</span>
            <span className="border-l border-hairline pl-3 hidden sm:inline">
              {ARCH_LAYERS.length} layers · {ARCH_NODES.length} components
            </span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
            hover · click · esc
          </span>
        </div>
      )}

      {variant === "full" && (
        <p className="font-serif text-ink-2 text-base md:text-lg leading-relaxed mb-6 max-w-4xl">
          {ELEVATOR_PITCH}
        </p>
      )}

      {/* Desktop diagram --------------------------------------------------- */}
      <div
        className={cn(
          "relative",
          // The mobile accordion replaces the SVG below `md`:
          variant === "full" ? "hidden md:block" : "hidden md:block",
        )}
      >
        <DiagramSurface
          variant={variant}
          activeId={activeId}
          related={related}
          interactive={interactive}
          onHover={setHoverId}
          onSelect={(id) => setFocusId((cur) => (cur === id ? null : id))}
        />
      </div>

      {/* Mobile accordion -------------------------------------------------- */}
      {variant === "full" && (
        <div className="md:hidden">
          <MobileAccordion onSchemaDeepLink={handleSchemaDeepLink} />
        </div>
      )}

      {/* Legend (full only) ----------------------------------------------- */}
      {variant === "full" && <ProtocolMapLegend />}

      {/* Detail panel (full only) ----------------------------------------- */}
      {variant === "full" && focusedNode && (
        <div className="mt-6">
          <ProtocolMapPanel
            node={focusedNode}
            onClose={() => setFocusId(null)}
            onNavigate={handleNavigateTo}
            onSchemaDeepLink={handleSchemaDeepLink}
          />
        </div>
      )}

      {/* Preview CTA ------------------------------------------------------- */}
      {variant === "preview" && (
        <div className="mt-4 flex justify-center">
          <a
            href={exploreHref}
            className="text-xs uppercase tracking-widest font-semibold text-ink border border-ink px-6 py-3 hover:bg-ink hover:text-paper transition-colors"
          >
            Explore the protocol map →
          </a>
        </div>
      )}

      {/* Off-screen list for screen readers -------------------------------- */}
      <ScreenReaderOutline />
    </div>
  );
}

// -----------------------------------------------------------------------------
// DiagramSurface — the SVG underlay + DOM overlay
// -----------------------------------------------------------------------------

function DiagramSurface({
  variant,
  activeId,
  related,
  interactive,
  onHover,
  onSelect,
}: {
  variant: "full" | "preview";
  activeId: string | null;
  related: Set<string> | null;
  interactive: boolean;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
}) {
  const { width: vbW, height: vbH } = MAP_VIEWBOX;

  return (
    <div
      role="img"
      aria-labelledby="protocol-map-title"
      className={cn(
        "relative w-full bg-paper",
        variant === "preview" && "overflow-hidden",
      )}
      style={{ aspectRatio: `${vbW} / ${vbH}` }}
    >
      <span id="protocol-map-title" className="sr-only">
        .person Protocol — system architecture map
      </span>

      {/* Layer guide rails (subtle horizontal banding) */}
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      >
        {ARCH_LAYERS.map((layer, i) => {
          const layerHeight = vbH / ARCH_LAYERS.length;
          const y = i * layerHeight;
          return (
            <g key={layer.id}>
              {/* alternating band */}
              {i % 2 === 1 && (
                <rect
                  x={0}
                  y={y}
                  width={vbW}
                  height={layerHeight}
                  fill="var(--color-paper-2)"
                  opacity={0.45}
                />
              )}
              {/* hairline divider */}
              {i > 0 && (
                <line
                  x1={0}
                  y1={y}
                  x2={vbW}
                  y2={y}
                  stroke="var(--color-hairline)"
                  strokeWidth={0.5}
                />
              )}
            </g>
          );
        })}

        {/* Edges */}
        <g aria-hidden="true">
          {ARCH_EDGES.map((edge, i) => {
            const from = getNode(edge.from);
            const to = getNode(edge.to);
            if (!from || !to) return null;
            const isActive =
              activeId !== null &&
              (related?.has(from.id) ?? false) &&
              (related?.has(to.id) ?? false) &&
              (edge.from === activeId || edge.to === activeId);
            const isDimmed = activeId !== null && !isActive;
            const props = strokeProps(edge.kind);
            return (
              <path
                key={`${edge.from}-${edge.to}-${i}`}
                d={edgePath(from, to)}
                fill="none"
                stroke={isActive ? "var(--color-ink)" : props.stroke}
                strokeWidth={isActive ? props.strokeWidth + 0.4 : props.strokeWidth}
                strokeDasharray={props.strokeDasharray}
                strokeLinecap="round"
                opacity={isDimmed ? 0.18 : 0.85}
                style={{ transition: "opacity 250ms, stroke-width 250ms, stroke 250ms" }}
              />
            );
          })}
        </g>
      </svg>

      {/* Layer titles — left-side strip */}
      <div className="absolute inset-0 pointer-events-none">
        {ARCH_LAYERS.map((layer, i) => {
          const layerHeight = vbH / ARCH_LAYERS.length;
          const topPct = (i * layerHeight / vbH) * 100;
          const heightPct = (layerHeight / vbH) * 100;
          const widthPct = (MAP_LAYER_LABEL_WIDTH / vbW) * 100;
          return (
            <div
              key={layer.id}
              className="absolute flex flex-col justify-center pl-3 md:pl-5 pr-3 border-r border-hairline/60"
              style={{
                top: `${topPct}%`,
                left: 0,
                width: `${widthPct}%`,
                height: `${heightPct}%`,
              }}
            >
              <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-widest text-ink-3 mb-0.5">
                Layer {i + 1}
              </div>
              <div className="font-serif text-sm md:text-base text-ink leading-tight">
                {layer.title}
              </div>
              <div className="font-serif italic text-[11px] md:text-[12px] text-ink-2 leading-tight mt-0.5 hidden md:block">
                {layer.caption}
              </div>
            </div>
          );
        })}
      </div>

      {/* Nodes */}
      <div className="absolute inset-0">
        {ARCH_NODES.map((node) => {
          const { x, y } = nodeCentre(node);
          const state = activeId
            ? related?.has(node.id)
              ? node.id === activeId
                ? "focused"
                : "idle"
              : "dimmed"
            : "idle";
          return (
            <ProtocolMapNode
              key={node.id}
              node={node}
              state={state as "idle" | "focused" | "dimmed"}
              cx={x}
              cy={y}
              width={MAP_NODE_WIDTH}
              height={MAP_NODE_HEIGHT}
              viewBoxWidth={vbW}
              viewBoxHeight={vbH}
              interactive={interactive}
              onHover={interactive ? onHover : undefined}
              onSelect={interactive ? onSelect : undefined}
            />
          );
        })}
      </div>

      {/* Floating tooltip */}
      {interactive && activeId && (
        <FloatingTooltip nodeId={activeId} />
      )}

      {/* Preview gradient fade */}
      {variant === "preview" && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-paper pointer-events-none"
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FloatingTooltip — anchored to the active node, follows it on screen
// -----------------------------------------------------------------------------

function FloatingTooltip({ nodeId }: { nodeId: string }) {
  const node = getNode(nodeId);
  const { x, y } = node ? nodeCentre(node) : { x: 0, y: 0 };
  if (!node) return null;
  const { width: vbW, height: vbH } = MAP_VIEWBOX;
  const leftPct = (x / vbW) * 100;
  const topPct = ((y - MAP_NODE_HEIGHT / 2 - 8) / vbH) * 100;
  return (
    <div
      role="tooltip"
      aria-hidden="true"
      className="absolute pointer-events-none -translate-x-1/2 -translate-y-full max-w-[260px] bg-ink text-paper px-3 py-2 shadow-[0_4px_12px_rgba(17,17,17,0.18)]"
      style={{
        left: `${leftPct}%`,
        top: `${topPct}%`,
      }}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-paper-2 mb-1">
        {node.label}
      </div>
      <div className="font-serif italic text-[12px] leading-snug">
        {node.tooltip}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MobileAccordion — vertical fallback for < md
// -----------------------------------------------------------------------------

function MobileAccordion({
  onSchemaDeepLink,
}: {
  onSchemaDeepLink: (schemaDomainId: string) => void;
}) {
  return (
    <div className="space-y-2">
      {ARCH_LAYERS.map((layer, i) => {
        const nodes = nodesByLayer(layer.id);
        return (
          <details
            key={layer.id}
            className="group border border-hairline bg-paper open:bg-paper-2/40"
            {...(i === 0 ? { open: true } : {})}
          >
            <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3">
                  Layer {i + 1}
                </div>
                <div className="font-serif text-base text-ink leading-tight">
                  {layer.title}
                </div>
                <div className="font-serif italic text-[12px] text-ink-2 leading-tight">
                  {layer.caption}
                </div>
              </div>
              <span aria-hidden="true" className="font-mono text-ink-3 text-xs shrink-0">
                <span className="group-open:hidden">[+]</span>
                <span className="hidden group-open:inline">[−]</span>
              </span>
            </summary>
            <ul role="list" className="border-t border-hairline divide-y divide-hairline">
              {nodes.map((node) => {
                const { incoming, outgoing } = edgesFor(node.id);
                return (
                  <li key={node.id} className="px-4 py-3">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-mono text-[12px] text-ink">
                        {node.label}
                      </span>
                      {node.planned && (
                        <span className="font-mono text-[8px] uppercase tracking-widest text-state-pending border border-state-pending/50 px-1 py-px leading-none">
                          planned
                        </span>
                      )}
                    </div>
                    {node.caption && (
                      <div className="font-serif italic text-[12px] text-ink-2 mb-1">
                        {node.caption}
                      </div>
                    )}
                    <p className="text-[13px] text-ink-2 leading-relaxed font-serif">
                      {node.tooltip}
                    </p>
                    {(outgoing.length > 0 || incoming.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {outgoing.map((e, j) => {
                          const other = getNode(e.to);
                          if (!other) return null;
                          return (
                            <span
                              key={`o-${j}`}
                              className="inline-flex items-baseline gap-1 px-1.5 py-0.5 border border-hairline text-[10px] font-mono"
                            >
                              <span className="text-ink-3 uppercase tracking-widest">
                                {edgeKindLabel(e.kind)}
                              </span>
                              <span className="text-ink">{other.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {node.schemaDomainId && (
                      <button
                        type="button"
                        onClick={() => onSchemaDeepLink(node.schemaDomainId!)}
                        className="mt-2 text-[10px] uppercase tracking-widest font-semibold text-ink underline decoration-hairline underline-offset-4 hover:decoration-ink"
                      >
                        Open in schema →
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        );
      })}
    </div>
  );
}

function edgeKindLabel(kind: EdgeKind): string {
  switch (kind) {
    case "anchors-to": return "anchors to";
    case "enforces":   return "enforces";
    case "stored-in":  return "stored in";
    case "governs":    return "governs";
  }
}

// -----------------------------------------------------------------------------
// Screen-reader outline — full textual representation of the diagram
// -----------------------------------------------------------------------------

function ScreenReaderOutline() {
  return (
    <div className="sr-only">
      <h3>Protocol map outline</h3>
      <ol>
        {ARCH_LAYERS.map((layer, i) => (
          <li key={layer.id}>
            <strong>Layer {i + 1} — {layer.title}.</strong> {layer.caption}.
            <ul>
              {nodesByLayer(layer.id).map((node) => {
                const { outgoing } = edgesFor(node.id);
                return (
                  <li key={node.id}>
                    <strong>{node.label}.</strong> {node.tooltip}
                    {outgoing.length > 0 && (
                      <>
                        {" "}Connects to:{" "}
                        {outgoing
                          .map((e) => {
                            const other = getNode(e.to);
                            return other ? `${other.label} (${edgeKindLabel(e.kind)})` : null;
                          })
                          .filter(Boolean)
                          .join(", ")}
                        .
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}
